/* SourceDetailModal — Detalhamento completo de uma Fonte (sources).
 *
 * Mostra título, autores, link original, PDF, metadados legados,
 * responsável original e usuário que inseriu/importou.
 */
import { useEffect, useState } from "react";
import { api, API_BASE, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { X, ExternalLink, FileText, Database, User, Calendar, Globe, Tag, BookOpen, Hash, Sprout, AlertCircle, Pencil, Save, Loader2 } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

const Field = ({ label, value, mono, icon: Icon }) => (
  <div>
    <div className="label-mil mb-0.5 flex items-center gap-1">
      {Icon && <Icon size={10} />}
      {label}
    </div>
    <div className={`text-sm text-[var(--text)] ${mono ? "font-mono text-xs" : ""}`}>
      {value || <span className="text-[var(--text-muted)]">—</span>}
    </div>
  </div>
);

const ENRICH_STATUS_LABEL = {
  PENDENTE_DE_ENRIQUECIMENTO: { label: "Pendente IA", className: "badge-warn" },
  ENRIQUECIDO: { label: "Enriquecida", className: "badge-success" },
  FALHA_ENRIQUECIMENTO: { label: "Falha IA", className: "badge-danger" },
  INSUFICIENTE: { label: "Insuficiente", className: "badge-danger" },
};

const STATUS_LINK_CLASS = {
  ATIVO: "badge-success",
  QUEBRADO: "badge-danger",
  DESCONHECIDO: "badge-warn",
};

export default function SourceDetailModal({ sourceId, onClose }) {
  const { user } = useAuth();
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState("");
  const [openSeed, setOpenSeed] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  const canEdit = user?.perfil === "admin" || user?.perfil === "coordenador";

  useEffect(() => {
    if (!sourceId) return;
    setSrc(null);
    setErr("");
    setEditing(false);
    setSaveMsg("");
    api
      .get(`/sources/${sourceId}`)
      .then((r) => setSrc(r.data))
      .catch((e) => setErr(formatErr(e)));
  }, [sourceId]);

  const startEdit = () => {
    if (!src) return;
    setForm({
      titulo: src.titulo || "",
      autores: Array.isArray(src.autores) ? src.autores.join(", ") : (src.autores || ""),
      ano_publicacao: src.ano_publicacao ?? "",
      idioma: src.idioma || "",
      pais_publicacao: src.pais_publicacao || "",
      tipo_fonte: src.tipo_fonte || "",
      instituicao: src.instituicao || "",
      doi: src.doi || "",
      link_original: src.link_original || "",
      link_drive: src.link_drive || "",
      abstract: src.abstract || "",
      tema_legado: src.tema_legado || "",
      observacoes_legado: src.observacoes_legado || "",
    });
    setEditing(true);
    setSaveMsg("");
    setErr("");
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm({});
    setSaveMsg("");
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    setSaveMsg("");
    try {
      const payload = { ...form };
      // converte string vazia -> null para campos opcionais
      ["doi", "link_original", "link_drive", "ano_publicacao"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      const { data } = await api.put(`/sources/${sourceId}`, payload);
      if (data.no_changes) {
        setSaveMsg("Nenhuma alteração detectada.");
      } else {
        setSrc(data.source);
        setSaveMsg(`Salvo · ${data.changed_fields?.length || 0} campo(s) atualizado(s).`);
        setEditing(false);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!sourceId) return null;

  const enrich = ENRICH_STATUS_LABEL[src?.status_enriquecimento];
  const inserter =
    src?.imported_by_user_name ||
    src?.created_by_user_name ||
    src?.researcher_user_name ||
    null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="source-detail-overlay"
      >
        <div
          className="card-mil w-full max-w-5xl my-8 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="source-detail-modal"
        >
          {/* Header */}
          <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3 gold-accent-top">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Database size={14} className="text-[var(--gold)]" />
                <span className="label-mil text-[var(--gold)]">Detalhamento · Fonte</span>
                {src?.legacy_id != null && (
                  <span className="badge badge-info text-[10px] font-mono">#{src.legacy_id}</span>
                )}
                {src?.origem_importacao && (
                  <span className="badge badge-muted text-[10px]">{src.origem_importacao}</span>
                )}
              </div>
              <h2 className="font-display font-bold text-xl text-[var(--text)] leading-tight">
                {src?.titulo || "Carregando…"}
              </h2>
              {src?.autores && src.autores.length > 0 && (
                <p className="text-xs text-[var(--text-muted)] mt-1 font-mono">
                  {src.autores.join(", ")}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canEdit && !editing && src && (
                <button
                  onClick={startEdit}
                  className="btn-ghost text-xs flex items-center gap-1.5"
                  data-testid="source-detail-edit"
                  title="Editar metadados"
                >
                  <Pencil size={12} /> Editar
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-[var(--surface-2)] rounded text-[var(--text-muted)] hover:text-[var(--text)]"
                data-testid="source-detail-close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto scrollbar-thin p-5">
            {err && (
              <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded mb-4 flex items-center gap-2">
                <AlertCircle size={14} /> {err}
              </div>
            )}
            {saveMsg && !editing && (
              <div className="text-sm text-[var(--success)] border border-[var(--success)]/40 bg-[var(--success)]/10 p-3 rounded mb-4">
                {saveMsg}
              </div>
            )}
            {!src && !err && (
              <div className="font-mono text-xs text-[var(--text-muted)] text-center py-12">
                CARREGANDO…
              </div>
            )}

            {/* === MODO EDIÇÃO === */}
            {src && editing && (
              <div className="space-y-4" data-testid="source-edit-form">
                <div className="card-mil p-3 text-[11px] font-mono text-[var(--text-muted)] flex items-center gap-2">
                  <Pencil size={11} className="text-[var(--gold)]" />
                  Editando metadados — alterações são registradas em audit-logs.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label="Título" required>
                    <input className="input-mil" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="src-edit-titulo" />
                  </FormField>
                  <FormField label="Autores (separados por vírgula)">
                    <input className="input-mil" value={form.autores} onChange={(e) => setForm({ ...form, autores: e.target.value })} data-testid="src-edit-autores" />
                  </FormField>
                  <FormField label="Ano">
                    <input type="number" className="input-mil" value={form.ano_publicacao} onChange={(e) => setForm({ ...form, ano_publicacao: e.target.value })} data-testid="src-edit-ano" />
                  </FormField>
                  <FormField label="Tipo">
                    <input className="input-mil" value={form.tipo_fonte} onChange={(e) => setForm({ ...form, tipo_fonte: e.target.value })} data-testid="src-edit-tipo" />
                  </FormField>
                  <FormField label="Idioma">
                    <input className="input-mil" value={form.idioma} onChange={(e) => setForm({ ...form, idioma: e.target.value })} data-testid="src-edit-idioma" />
                  </FormField>
                  <FormField label="País">
                    <input className="input-mil" value={form.pais_publicacao} onChange={(e) => setForm({ ...form, pais_publicacao: e.target.value })} data-testid="src-edit-pais" />
                  </FormField>
                  <FormField label="Instituição">
                    <input className="input-mil" value={form.instituicao} onChange={(e) => setForm({ ...form, instituicao: e.target.value })} data-testid="src-edit-instituicao" />
                  </FormField>
                  <FormField label="DOI">
                    <input className="input-mil font-mono text-xs" value={form.doi} onChange={(e) => setForm({ ...form, doi: e.target.value })} data-testid="src-edit-doi" />
                  </FormField>
                  <FormField label="Link original" className="md:col-span-2">
                    <input className="input-mil font-mono text-xs" value={form.link_original} onChange={(e) => setForm({ ...form, link_original: e.target.value })} placeholder="https://…" data-testid="src-edit-link" />
                  </FormField>
                  <FormField label="Link Drive (excepcional)" className="md:col-span-2">
                    <input className="input-mil font-mono text-xs" value={form.link_drive} onChange={(e) => setForm({ ...form, link_drive: e.target.value })} placeholder="https://drive.google.com/…" data-testid="src-edit-drive" />
                  </FormField>
                  <FormField label="Tema (legado)" className="md:col-span-2">
                    <input className="input-mil" value={form.tema_legado} onChange={(e) => setForm({ ...form, tema_legado: e.target.value })} data-testid="src-edit-tema" />
                  </FormField>
                  <FormField label="Resumo" className="md:col-span-2">
                    <textarea className="input-mil min-h-[120px]" value={form.abstract} onChange={(e) => setForm({ ...form, abstract: e.target.value })} data-testid="src-edit-abstract" />
                  </FormField>
                  <FormField label="Observações (legado)" className="md:col-span-2">
                    <textarea className="input-mil min-h-[80px]" value={form.observacoes_legado} onChange={(e) => setForm({ ...form, observacoes_legado: e.target.value })} data-testid="src-edit-obs" />
                  </FormField>
                </div>
              </div>
            )}

            {/* === MODO VISUALIZAÇÃO === */}
            {src && !editing && (
              <div className="space-y-5">
                {/* Ações rápidas */}
                <div className="flex flex-wrap gap-2">
                  {src.link_original && (
                    <a
                      href={src.link_original}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-gold text-xs flex items-center gap-1.5"
                      data-testid="source-detail-link"
                    >
                      <ExternalLink size={12} /> Abrir link da fonte
                    </a>
                  )}
                  {src.has_pdf && (
                    <a
                      href={`${API_BASE}/sources/${src.source_id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost text-xs flex items-center gap-1.5"
                      data-testid="source-detail-pdf"
                    >
                      <FileText size={12} /> Abrir PDF ({src.pdf_filename})
                    </a>
                  )}
                  {src.link_drive && (
                    <a
                      href={src.link_drive}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost text-xs flex items-center gap-1.5"
                      data-testid="source-detail-drive"
                    >
                      <ExternalLink size={12} /> Drive
                    </a>
                  )}
                  {src.doi && (
                    <a
                      href={`https://doi.org/${src.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost text-xs flex items-center gap-1.5"
                      data-testid="source-detail-doi"
                    >
                      <Hash size={12} /> DOI
                    </a>
                  )}
                </div>

                {/* Metadata grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Field label="Tipo" value={src.tipo_fonte} icon={BookOpen} />
                  <Field label="Ano" value={src.ano_publicacao} icon={Calendar} mono />
                  <Field label="Idioma" value={src.idioma} icon={Globe} mono />
                  <Field label="País" value={src.pais_publicacao} icon={Globe} />
                  <Field label="Instituição" value={src.instituicao} icon={Database} />
                  <Field label="DOI" value={src.doi} mono />
                  <Field label="Origem" value={src.origem_fonte || src.origem_importacao} icon={Tag} />
                  <Field label="Status do Link" value={
                    src.status_link ? (
                      <span className={`badge ${STATUS_LINK_CLASS[src.status_link] || "badge-muted"} text-[10px]`}>
                        {src.status_link}
                      </span>
                    ) : null
                  } />
                </div>

                {/* Responsável legado */}
                {(src.pesquisador_responsavel_original || src.responsavel_legado) && (
                  <div className="card-mil p-3">
                    <div className="label-mil mb-1 flex items-center gap-1">
                      <User size={10} /> Responsável original (planilha)
                    </div>
                    <div className="text-sm font-semibold">
                      {src.pesquisador_responsavel_original || src.responsavel_legado}
                    </div>
                  </div>
                )}

                {/* === RESPONSABILIDADE — papéis separados === */}
                <div className="border border-[var(--border)] rounded p-4 bg-[var(--surface-2)]/30">
                  <div className="label-mil text-[var(--gold)] mb-3 flex items-center gap-1.5">
                    <User size={11} /> Responsabilidade
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <RoleField
                      label="Pesquisado por"
                      value={src.researcher_user_name || src.pesquisador_responsavel_original}
                      hint={src.pesquisador_responsavel_original && src.researcher_user_name ? `Nome original na planilha: ${src.pesquisador_responsavel_original}` : null}
                      fallback="Não informado"
                    />
                    <RoleField
                      label="Importado por"
                      value={src.imported_by_user_name}
                      hint={src.import_filename ? `Arquivo: ${src.import_filename}` : null}
                      fallback="Não informado"
                    />
                    <RoleField
                      label="Enriquecido por IA"
                      value={src.enriched_by_user_name}
                      hint={src.enriched_at ? `em ${fmtDate(src.enriched_at)}` : null}
                      fallback="Ainda não enriquecido"
                    />
                    <RoleField
                      label="Última edição de metadados"
                      value={src.last_edited_by_user_name}
                      hint={src.updated_at ? `em ${fmtDate(src.updated_at)}` : null}
                      fallback="Sem edições manuais"
                    />
                  </div>
                </div>

                {/* Resumo */}
                {(src.abstract || src.resumo_legado) && (
                  <div>
                    <div className="label-mil mb-1">Resumo</div>
                    <p className="text-sm text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                      {src.abstract || src.resumo_legado}
                    </p>
                  </div>
                )}

                {/* Tema legado */}
                {src.tema_legado && (
                  <Field label="Tema (legado)" value={src.tema_legado} icon={Tag} />
                )}

                {/* Observações */}
                {src.observacoes_legado && (
                  <Field label="Observações (legado)" value={src.observacoes_legado} mono />
                )}

                {/* Palavras-chave */}
                {src.palavras_chave && src.palavras_chave.length > 0 && (
                  <div>
                    <div className="label-mil mb-1">Palavras-chave</div>
                    <div className="flex flex-wrap gap-1">
                      {src.palavras_chave.map((p) => (
                        <span key={`kw-${p}`} className="badge badge-muted text-[10px]">{p}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enriquecimento IA */}
                {src.origem_importacao === "Base Legada LSC" && (
                  <div className="border border-[var(--border)] rounded p-4 bg-[var(--surface-2)]/40">
                    <div className="label-mil text-[var(--gold)] mb-2">Enriquecimento por IA</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <div className="text-[10px] text-[var(--text-muted)] uppercase">Status</div>
                        {enrich ? (
                          <span className={`badge ${enrich.className} text-[10px]`}>{enrich.label}</span>
                        ) : "—"}
                      </div>
                      <div>
                        <div className="text-[10px] text-[var(--text-muted)] uppercase">Tentativas</div>
                        <div className="text-sm font-mono">{src.enrichment_attempts ?? 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-[var(--text-muted)] uppercase">Último processamento</div>
                        <div className="text-xs font-mono">{fmtDate(src.last_enrichment_at)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] text-[var(--text-muted)] uppercase">Semente gerada</div>
                        {src.enriched_seed_id ? (
                          <button
                            onClick={() => setOpenSeed(src.enriched_seed_id)}
                            className="btn-ghost text-[11px] flex items-center gap-1"
                            data-testid="source-detail-open-seed"
                          >
                            <Sprout size={11} /> Ver semente
                          </button>
                        ) : (
                          <span className="text-[var(--text-muted)] text-sm">—</span>
                        )}
                      </div>
                    </div>
                    {src.last_enrichment_error && (
                      <div className="text-[11px] text-[var(--danger)] mt-2 font-mono">
                        Erro: {src.last_enrichment_error}
                      </div>
                    )}
                  </div>
                )}

                {/* Auditoria — inserção */}
                <div className="border-t border-[var(--border)] pt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <Field
                    label="Inserido por"
                    value={inserter}
                    icon={User}
                  />
                  <Field
                    label="Importado em"
                    value={fmtDate(src.imported_at || src.created_at)}
                    mono
                  />
                  <Field
                    label="Arquivo de origem"
                    value={src.import_filename}
                    mono
                  />
                  <Field label="Source ID" value={src.source_id} mono />
                  <Field
                    label="Insumos vinculados"
                    value={src.inputs_count != null ? String(src.inputs_count) : "—"}
                    mono
                  />
                  <Field
                    label="Sementes vinculadas"
                    value={src.seeds_count != null ? String(src.seeds_count) : "—"}
                    mono
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-[var(--border)] flex justify-between items-center gap-2 bg-[var(--bg)]/40">
            <div className="text-[10px] font-mono text-[var(--text-muted)]">
              {editing && (
                <span className="text-[var(--gold)] flex items-center gap-1">
                  <Pencil size={10} /> EDITANDO — clique em Salvar para confirmar
                </span>
              )}
              {!editing && src?.last_edited_by_user_name && (
                <span>Última edição: {src.last_edited_by_user_name} · {fmtDate(src.updated_at)}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editing ? (
                <>
                  <button onClick={cancelEdit} className="btn-ghost text-xs" disabled={saving} data-testid="source-edit-cancel">
                    Cancelar
                  </button>
                  <button
                    onClick={save}
                    className="btn-gold text-xs flex items-center gap-1.5 disabled:opacity-50"
                    disabled={saving || !form.titulo?.trim()}
                    data-testid="source-edit-save"
                  >
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Salvar alterações
                  </button>
                </>
              ) : (
                <button onClick={onClose} className="btn-ghost text-xs" data-testid="source-detail-fechar">
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} />
    </>
  );
}

function FormField({ label, required, className = "", children }) {
  return (
    <div className={className}>
      <label className="label-mil mb-1 block">
        {label}
        {required && <span className="text-[var(--gold)] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function RoleField({ label, value, hint, fallback }) {
  const hasValue = value && String(value).trim();
  const slug = String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <div data-testid={`role-field-${slug}`}>
      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-widest font-mono mb-0.5">{label}</div>
      <div className={`text-sm ${hasValue ? "text-[var(--text)] font-semibold" : "text-[var(--text-muted)] italic"}`}>
        {hasValue ? value : (fallback || "Não informado")}
      </div>
      {hint && hasValue && (
        <div className="text-[10px] font-mono text-[var(--text-muted)] mt-0.5">{hint}</div>
      )}
    </div>
  );
}
